import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import authService from '../../services/authService';
import './Auth.css';

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralToken: '',
  });

  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tokenValidating, setTokenValidating] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    const token = searchParams.get('referralToken');
    if (token) {
      validateReferralToken(token);
    }
  }, [searchParams]);

  const validateReferralToken = async (token) => {
    setTokenValidating(true);
    try {
      const response = await authService.validateReferralToken(token);

      if (response.success) {
        setFormData(prev => ({
          ...prev,
          referralToken: token,
        }));

        setReferralInfo({
          referrerName: response.referrerName,
          referrerEmail: response.referrerEmail,
          referralCode: response.referralCode,
          remainingReferrals: response.remainingReferrals,
        });

        setError('');
      } else {
        setError(response.message || 'Invalid referral token');
      }
    } catch (err) {
      setError(err.message || 'Failed to validate referral token');
    } finally {
      setTokenValidating(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');

    if (name === 'password') {
      calculatePasswordStrength(value);
    }
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;
    setPasswordStrength(strength);
  };

  const getPasswordStrengthLabel = () => {
    const labels = ['Weak', 'Fair', 'Good', 'Strong'];
    return labels[passwordStrength - 1] || '';
  };

  const getPasswordStrengthColor = () => {
    const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e'];
    return colors[passwordStrength - 1] || '#e5e7eb';
  };

  const validateForm = () => {
    if (!formData.fullName || !formData.email || !formData.password || !formData.confirmPassword) {
      setError('All fields are required');
      return false;
    }

   if (!formData.referralToken) {
     setError('You need a valid invitation link to sign up. Please check your email.');
     return false;
   }
    if (formData.fullName.trim().length < 2) {
      setError('Full name must be at least 2 characters');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await authService.signup(
        formData.email,
        formData.password,
        formData.fullName,
        formData.referralToken
      );

      if (response.success) {
        navigate('/verify-email-check', {
          state: {
            email: formData.email,
            message: 'Account created! Please check your email to verify your account.'
          }
        });
      }
    } catch (err) {
      setError(err.message || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignupSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    setError('');

    try {
      // Check if user has referral token
      if (!formData.referralToken) {
        setError('You need a valid invitation to sign up. Please check your email for an invitation link.');
        setGoogleLoading(false);
        return;
      }

      // Send Google credential WITH referral token
      const response = await authService.googleSignupWithReferral(
        credentialResponse.credential,
        formData.referralToken
      );

      if (response.success) {
        localStorage.setItem('accessToken', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.setItem('user', JSON.stringify(response.user));
        navigate('/dashboard');
      } else {
        setError(response.message || 'Google signup failed');
      }
    } catch (err) {
      setError(err.message || 'Google authentication failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleSignupError = () => {
    setError('Google signup failed. Please try again.');
  };

  return (
    <div className="auth-container-gh">
      <div className="auth-brand">
        <h1>AI Recruitment System</h1>
        <p>Hire smarter with AI-powered candidate evaluation</p>
      </div>

      <div className="auth-card-gh">
        <div className="auth-card-header">
          <h2>Create your account</h2>
          <p>Get started with AI-powered recruitment</p>
        </div>

        {referralInfo && (
          <div className="referral-info-box">
            <strong>âœ… Invited by {referralInfo.referrerName}</strong>
            <p>Complete your registration to accept the invitation</p>
          </div>
        )}
        {!formData.referralToken && (
          <div className="alert-gh alert-info-gh" style={{
            background: '#dbeafe',
            border: '1px solid #3b82f6',
            color: '#1e40af',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px'
          }}>
            <strong>ðŸ“§ Need an invitation?</strong>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
              You can only sign up with a valid invitation link.
              Ask an existing user to send you an invitation from their Profile page.
            </p>
          </div>
        )}


        {error && (
          <div className="alert-gh alert-error-gh">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {error}
          </div>
        )}

        <div className="google-btn-wrapper">
          <GoogleLogin
            onSuccess={handleGoogleSignupSuccess}
            onError={handleGoogleSignupError}
            text="signup_with"
            theme="outline"
            size="large"
            width="100%"
          />
        </div>

        <div className="divider-gh">
          <span>or</span>
        </div>

        <form onSubmit={handleSubmit} className="auth-form-gh">
          <div className="form-group-gh">
            <label htmlFor="fullName">Full name</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Enter your full name"
              disabled={loading || googleLoading || tokenValidating}
            />
          </div>

          <div className="form-group-gh">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@company.com"
              disabled={loading || googleLoading || tokenValidating}
            />
          </div>

          <div className="form-group-gh">
            <label htmlFor="password">
              Password
              {formData.password && (
                <span className="password-strength-label" style={{ color: getPasswordStrengthColor() }}>
                  {getPasswordStrengthLabel()}
                </span>
              )}
            </label>
            <div className="password-wrapper-gh">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a strong password"
                disabled={loading || googleLoading || tokenValidating}
              />
              <button
                type="button"
                className="password-toggle-gh"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading || googleLoading || tokenValidating}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                )}
              </button>
            </div>
            {formData.password && (
              <div className="password-strength-bar">
                <div
                  className="strength-fill"
                  style={{
                    width: `${(passwordStrength / 4) * 100}%`,
                    backgroundColor: getPasswordStrengthColor(),
                  }}
                ></div>
              </div>
            )}
          </div>

          <div className="form-group-gh">
            <label htmlFor="confirmPassword">Confirm password</label>
            <div className="password-wrapper-gh">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                disabled={loading || googleLoading || tokenValidating}
              />
              <button
                type="button"
                className="password-toggle-gh"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={loading || googleLoading || tokenValidating}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button type="submit" className="btn-primary-gh-full" disabled={loading || googleLoading || tokenValidating}>
            {loading ? (
              <>
                <div className="spinner-small"></div>
                Creating account...
              </>
            ) : (
              'Create account'
            )}
          </button>
        </form>

        <div className="auth-footer-gh">
          <p>Already have an account? <Link to="/login">Sign in</Link></p>
        </div>
      </div>
    </div>
  );
};

export default Signup;